import { useEffect, useMemo, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DespachoDirectChatHeader } from "@/components/despacho/DespachoAuxiliarQuickActions";
import { mockDirectChatByAuxiliar } from "@/modules/despacho/despachoMockData";

/**
 * Panel mock — chat directo 1:1 con auxiliar (sin persistencia).
 */
export default function DespachoDirectChatPanel({
  auxiliar,
  pendingContext = null,
  draftMessage = "",
  onClose,
}) {
  const [messages, setMessages] = useState(() => {
    const seed = mockDirectChatByAuxiliar[auxiliar?.id] || [];
    return [...seed];
  });
  const [composer, setComposer] = useState(draftMessage || "");

  useEffect(() => {
    setComposer(draftMessage || "");
  }, [draftMessage, auxiliar?.id]);

  const auxiliarNombre = auxiliar?.nombre || "Auxiliar";

  const handleSend = () => {
    const text = composer.trim();
    if (!text) return;
    setMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        from: "Supervisor",
        text,
        at: "Ahora",
        mine: true,
      },
    ]);
    setComposer("");
  };

  const threadKey = useMemo(() => `${auxiliar?.id}-${pendingContext?.titulo || ""}`, [auxiliar?.id, pendingContext?.titulo]);

  if (!auxiliar?.id) return null;

  return (
    <section
      className="rounded-sm border border-[#0F2D5C]/20 bg-slate-50/60 p-4 space-y-3"
      data-testid="despacho-direct-chat-panel"
      aria-label={`Chat directo con ${auxiliarNombre}`}
      key={threadKey}
    >
      <DespachoDirectChatHeader
        auxiliarNombre={auxiliarNombre}
        pendingContext={pendingContext}
        onClose={onClose}
      />

      <ul className="space-y-2 m-0 p-0 list-none max-h-48 overflow-y-auto">
        {messages.map((msg) => (
          <li
            key={msg.id}
            className={`rounded-sm px-3 py-2 text-xs max-w-[92%] ${
              msg.mine
                ? "ml-auto bg-[#0F2D5C] text-white"
                : "mr-auto bg-white border border-slate-200 text-slate-800"
            }`}
          >
            {!msg.mine ? <span className="block text-[10px] font-semibold opacity-80 mb-0.5">{msg.from}</span> : null}
            <p className="m-0 leading-relaxed">{msg.text}</p>
            <span className={`block text-[10px] mt-1 ${msg.mine ? "text-white/70" : "text-slate-400"}`}>{msg.at}</span>
          </li>
        ))}
      </ul>

      <div className="flex gap-2 pt-1">
        <Input
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          placeholder="Escriba un mensaje directo (demostración)…"
          className="h-9 text-xs rounded-sm flex-1"
          data-testid="despacho-direct-chat-composer"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          className="rounded-sm h-9 bg-[#0F2D5C] hover:bg-[#0a2248] shrink-0"
          onClick={handleSend}
          data-testid="despacho-direct-chat-send"
        >
          <Send className="w-3.5 h-3.5" aria-hidden />
          <span className="sr-only">Enviar</span>
        </Button>
      </div>
    </section>
  );
}
